<template>
  <div class="px-4 pb-6">
    <div v-if="!customers?.items?.length" class="flex flex-col justify-center items-center py-6">
      <p class="text-sm text-center text-gray-500 dark:text-gray-400 mb-3">
        {{ $t('pages.course.view.noCustomersYet') }}
      </p>
      <o-panel-link
        v-if="canEnrolCourses"
        tag="o-button"
        variant="primary"
        :disabled="isFull"
        :path="`/customer/course/add?idCourseRun=${courseRun.idCourseRun}`"
      >
        {{ $t('pages.course.view.enrolCustomer') }}
      </o-panel-link>
    </div>

    <template v-if="customers?.items?.length">
      <div class="flex items-center justify-between mb-3">
        <p class="text-gray-700 dark:text-gray-200">
          <span class="font-semibold">{{ $t('pages.course.edit.customersCount', { count: courseRun.customerCounts.customersCount }) }}</span>
          <span v-if="courseRun.customerCounts.lowestCustomerMax">{{ $t('pages.course.edit.maxCapacity', { max: courseRun.customerCounts.lowestCustomerMax }) }}</span>
        </p>
        <o-panel-link
          v-if="canEnrolCourses"
          tag="o-button"
          size="sm"
          variant="primary"
          :disabled="isFull"
          :path="`/customer/course/add?idCourseRun=${courseRun.idCourseRun}`"
        >
          {{ $t('pages.course.view.enrolCustomer') }}
        </o-panel-link>
      </div>

      <div v-for="group in customerGroups" :key="group.key" class="mt-4 first:mt-0">
        <p
          v-if="group.label"
          class="mb-2 text-sm font-semibold text-gray-500 dark:text-gray-400"
        >
          {{ group.label }}
        </p>
        <div class="border rounded-lg dark:border-gray-800">
          <div
            v-for="customer in group.items"
            :key="customer.idCustomerCourse"
            class="w-full flex items-center justify-between text-left p-3 border-b dark:border-gray-800 last:border-none"
          >
            <div class="flex items-center">
              <o-avatar :items="[customer.customer]" size="md" border="" class="flex-shrink-0" />
              <div class="ml-3">
                <p class="font-medium leading-none">
                  {{ customer.customer.firstName }} {{ customer.customer.lastName }}
                </p>
                <p v-if="customer.customer.email" class="mt-1 text-gray-600 dark:text-gray-400 text-sm leading-none">
                  {{ customer.customer.email }}
                </p>
              </div>
            </div>

            <div class="flex items-center space-x-1">
              <span v-if="customer.payment?.price" class="text-sm text-gray-600 dark:text-gray-400 mr-1">
                {{ $currency.format(customer.payment.price) }}
              </span>

              <o-chip
                v-if="customer.payment?.schemeName"
                size="sm"
                flat
                variant="primary"
              >
                {{ customer.payment.schemeName }}
              </o-chip>

              <o-chip
                v-if="customer.payment?.isEarly"
                size="sm"
                flat
                variant="info"
              >
                {{ $t('general.earlyBird') }}
              </o-chip>

              <o-chip
                v-if="customer.isPaid"
                size="sm"
                flat
                variant="success"
              >
                {{ $t('pages.customer.course.id.paid') }}
              </o-chip>

              <o-chip
                v-if="customer.status === API.CustomerCourseStatus.cancelled"
                size="sm"
                flat
                variant="danger"
              >
                {{ $t('pages.customer.course.status.cancelled') }}
              </o-chip>

              <o-chip
                v-if="customer.eligibleMakeupCount"
                size="sm"
                flat
                variant="warning"
              >
                {{ $t('pages.customer.course.eligibleMakeups', { count: customer.eligibleMakeupCount }, customer.eligibleMakeupCount) }}
              </o-chip>

              <o-button
                v-if="customer.makeupDetails?.makeupAvailable && !customer.makeupDetails?.makeupScheduled"
                size="sm"
                variant="warning"
                @click="handleBookMakeup(customer)"
              >
                {{ $t('pages.customer.course.id.scheduleMakeup') }}
              </o-button>

              <template v-if="mode === 'view'">
                <o-tooltip v-if="!customer.order && !customer.isPaid" :text="$t('pages.course.view.newSale')">
                  <o-button
                    size="sm"
                    flat
                    variant="primary"
                    icon="sale"
                    @click="$emit('create-order', customer)"
                  />
                </o-tooltip>

                <o-tooltip v-if="customer.order" :text="$t('pages.course.view.viewSale')">
                  <o-panel-link
                    tag="o-button"
                    size="sm"
                    flat
                    icon="sale"
                    :path="`/order/${customer.order.idOrder}`"
                  />
                </o-tooltip>
              </template>

              <o-panel-link
                tag="o-button"
                size="sm"
                flat
                outline
                :icon="mode === 'edit' ? 'edit' : 'view'"
                :path="`/customer/course/${customer.idCustomerCourse}`"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <book-makeup-dialog
      ref="bookMakeupDialog"
      :portal="panel?.portal"
      :loading="loading"
      @update:loading="loading = $event"
    />
  </div>
</template>

<script>
import BookMakeupDialog from '@/components/customerCourse/actions/BookMakeup.vue'

import { OAvatar } from '@/components/elements'

import { CustomerCourseStatus } from '@/_api/models/course'

const API = {
  CustomerCourseStatus,
}

export default {
  components: {
    OAvatar,
    BookMakeupDialog,
  },
  inject: {
    panel: { default: null },
  },
  props: {
    courseRun: {
      type: Object,
      required: true,
    },
    customers: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: 'view',
      validator: value => ['view', 'edit'].includes(value),
    },
  },
  data () {
    return {
      API,
      loading: false,
    }
  },
  methods: {
    handleBookMakeup (customer) {
      // Book the make-up for the row's eligible missed session. The enrolment list
      // refreshes via the CustomerCourse bus event the dialog emits on success.
      this.$refs.bookMakeupDialog.open(customer, customer.idReservationCustomer)
    },
  },
  computed: {
    canEnrolCourses () {
      return this.$gates.get('courses') && this.$grants.all({ customerCourse: ['create'] })
    },
    activeCustomers () {
      return this.customers?.items?.filter(c => c.status !== API.CustomerCourseStatus.cancelled) || []
    },
    cancelledCustomers () {
      return this.customers?.items?.filter(c => c.status === API.CustomerCourseStatus.cancelled) || []
    },
    customerGroups () {
      const groups = []
      if (this.activeCustomers.length) {
        groups.push({ key: 'active', label: null, items: this.activeCustomers })
      }
      if (this.cancelledCustomers.length) {
        groups.push({ key: 'cancelled', label: this.$t('pages.customer.course.status.cancelled'), items: this.cancelledCustomers })
      }
      return groups
    },
    isFull () {
      const available = this.courseRun?.customerCounts?.lowestSpace
      return available === 0
    },
  },
}
</script>
